import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Star, MapPin, MessageSquareLock, HandCoins, LifeBuoy, ArrowRight, Sparkles, Briefcase, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useSeo } from "../components/kit";
import { ServiceInput, LocationInput } from "../components/Inputs";
import ProviderCard from "../components/ProviderCard";
import { AdvertCard } from "./Adverts";
import { UrgentJobsHome } from "../components/Urgent";
import PopularServices from "../components/PopularServices";
import LastMinuteDeals from "../components/LastMinuteDeals";
import { CategoryIcon } from "../components/CategoryIcon";

function FeaturedAdverts() {
  const [ads, setAds] = useState([]);
  useEffect(() => { api.get("/adverts", { params: { featured: 1 } }).then((r) => setAds(r.data.slice(0, 8))); }, []);
  if (!ads.length) return null;
  return (
    <section className="bg-[#FAF7EE]" data-testid="home-featured-adverts">
      <div className="container-x py-16 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div><span className="eyebrow">Featured services</span><h2 className="h2 mt-2">Ready-to-book services from local pros</h2></div>
          <Link to="/adverts" className="btn-ghost" data-testid="home-featured-adverts-all">Browse all services <ArrowRight size={16} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 stagger">{ads.map((a) => <AdvertCard key={a.id} a={a} />)}</div>
      </div>
    </section>
  );
}

function HeroSearch() {
  const [tab, setTab] = useState("find");
  const [svc, setSvc] = useState("");
  const [cat, setCat] = useState(null);
  const [loc, setLoc] = useState("");
  const [smart, setSmart] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams({ q: svc, location: loc });
    if (cat) p.set("category", cat);
    nav(`/search?${p}`);
  };
  const runSmart = async () => {
    if (!smart.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/search/smart", { text: smart });
      const p = new URLSearchParams({ q: data.job || "", location: data.location || "", smart: data.summary || smart });
      if (data.category) p.set("category", data.category);
      if (data.urgency === "emergency") p.set("emergency", "1");
      nav(`/search?${p}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white/95 backdrop-blur-md p-4 sm:p-6 rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-100" data-testid="hero-search">
      <div className="inline-flex p-1 bg-slate-100 rounded-xl mb-4" role="tablist">
        {[["find", "Find a Professional", Search], ["post", "Post a Job", Briefcase]].map(([k, l, I]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`hero-tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${tab === k ? "bg-[#374B5C] text-white shadow" : "text-slate-500 hover:text-[#374B5C]"}`}><I size={15} />{l}</button>
        ))}
      </div>
      {tab === "find" ? (
        <form onSubmit={go} className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <div><span className="label">What service do you need?</span><ServiceInput value={svc} onChange={setSvc} onPick={setCat} testid="hero-service-input" /></div>
          <div><span className="label">Where do you need it?</span><LocationInput value={loc} onChange={setLoc} testid="hero-location-input" /></div>
          <button className="btn-coral md:self-end h-[46px]" data-testid="hero-search-btn">Search Professionals</button>
        </form>
      ) : (
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div><span className="label">What do you need doing?</span><ServiceInput value={svc} onChange={setSvc} onPick={setCat} testid="hero-post-service-input" /></div>
          <button onClick={() => nav(`/post-job?${new URLSearchParams({ category: cat || "", q: svc })}`)} className="btn-coral md:self-end h-[46px]" data-testid="hero-post-job-btn">Post a Job — it's free</button>
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#374B5C] mb-2"><Sparkles size={14} className="text-[#FF8166]" />Or just describe it</div>
        <div className="flex gap-2">
          <input className="input" value={smart} onChange={(e) => setSmart(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSmart()}
            placeholder="I need someone to repair a leaking shower tomorrow in Marlow" data-testid="smart-search-input" aria-label="Describe what you need" />
          <button onClick={runSmart} disabled={busy} className="btn-navy shrink-0" data-testid="smart-search-btn">{busy ? "Thinking…" : "Go"}</button>
        </div>
      </div>
    </div>
  );
}

const HeroTitle = ({ text }) => {
  const k = "Your Trusted Partner";
  return text.startsWith(k) ? <><span className="text-[#FF8267] block">{k}</span>{text.slice(k.length).trim()}</> : text;
};

export default function Home() {
  const { cats } = useAuth();
  const [page, setPage] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/cms/home").then((r) => setPage(r.data));
    api.get("/search/providers", { params: { location: "Marlow", radius: 60 } }).then((r) => setFeatured(r.data.results.slice(0, 3)));
    api.get("/stats/public").then((r) => setStats(r.data));
  }, []);
  useSeo(page?.title?.replace(" | MyHomelyHub", ""), page?.meta_description, { "@context": "https://schema.org", "@type": "Organization", name: "MyHomelyHub Ltd", url: window.location.origin, email: "support@myhomelyhub.com" });
  const trust = [[ShieldCheck, "Verified and Trusted Professionals"], [Star, "Transparent reviews"], [MapPin, "Local Services at Your Fingertips"], [MessageSquareLock, "Secure messaging"], [HandCoins, "Convenient Booking and Payments"], [LifeBuoy, "We're here to help"]];
  return (
    <div>
      <section className="relative overflow-hidden bg-[#FAF7EE]" data-testid="hero-section">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-8 items-center">
          <div className="stagger">
            <span className="eyebrow">MyHomelyHub</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1E293B] mt-3 leading-[1.1]" data-testid="hero-title"><HeroTitle text={page?.hero_title || "Your Trusted Partner To Find The Right Services"} /></h1>
            <p className="text-base md:text-lg text-slate-600 mt-5 max-w-xl">{page?.hero_subtitle}</p>
            <div className="mt-8"><HeroSearch /></div>
            <ul className="flex flex-wrap gap-x-5 gap-y-2 mt-6 text-sm text-slate-600">
              {["Local professionals", "Verified reviews", "Simple job posting", "Secure communication", "No obligation to hire"].map((t) => <li key={t} className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald-600" />{t}</li>)}
            </ul>
          </div>
          <div className="hidden lg:flex justify-start relative fade-up">
            <img src="/brand/home_top.webp" alt="MyHomelyHub home services" className="ml-[10px] w-[calc(100%-10px)] max-w-none 2xl:w-[112%]" data-testid="hero-graphic" />
          </div>
        </div>
      </section>

      <section className="container-x py-16 lg:py-24">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div><span className="eyebrow">Popular Service Categories</span><h2 className="h2 mt-2">Whatever homely service you require</h2><p className="muted mt-2">You will find the perfect service provider.</p></div>
          <Link to="/services" className="btn-ghost btn-sm hidden sm:inline-flex" data-testid="all-categories-link">All Categories <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 stagger">
          {cats.slice(0, 10).map((c) => (
            <Link key={c.slug} to={`/search?category=${c.slug}`} className="card-hover p-5 group" data-testid={`category-card-${c.slug}`}>
              <div className="w-12 h-12 rounded-2xl bg-[#FFF1EE] grid place-items-center mb-4 group-hover:scale-110 transition-transform">
                <CategoryIcon icon={c.icon} name={c.name} size={28} />
              </div>
              <div className="font-semibold text-slate-800 text-sm">{c.name}</div>
              <div className="text-xs text-slate-500 mt-1">{c.subcategories.length} services</div>
            </Link>
          ))}
        </div>
      </section>

      <PopularServices />

      <LastMinuteDeals />

      <FeaturedAdverts />

      <UrgentJobsHome />

      <section className="bg-[#374B5C] text-white">
        <div className="container-x py-16 lg:py-24">
          <span className="eyebrow">How it works</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-2 max-w-xl">MyHomelyHub can help you get jobs done!</h2>
          <p className="text-slate-300 mt-2">Posting an ad is easy – it only takes a few simple steps!</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10 stagger">
            {(page?.sections || []).map((s, i) => (
              <div key={s.heading} className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors">
                <div className="text-[#FF8166] font-display font-extrabold text-3xl">0{i + 1}</div>
                <h3 className="font-semibold text-lg mt-3">{s.heading}</h3>
                <p className="text-sm text-slate-300 mt-2">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/post-job" className="btn-coral" data-testid="hiw-post-job-btn">Post a Job</Link>
            <Link to="/how-it-works" className="btn bg-white/10 hover:bg-white/20 text-white">See how it works</Link>
          </div>
        </div>
      </section>

      <section className="container-x py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <img src="/brand/home_professionals.webp" alt="Home service professionals" className="w-full max-w-md mx-auto" loading="lazy" />
        <div>
          <span className="eyebrow">Your One-Stop Solution for Home Services</span>
          <h2 className="h2 mt-2">Everything your home needs, all in one place</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {trust.map(([I, t]) => <div key={t} className="flex gap-3 items-start"><span className="w-10 h-10 rounded-xl bg-[#EBF0F4] text-[#374B5C] grid place-items-center shrink-0"><I size={18} /></span><span className="font-medium text-slate-700 text-sm pt-2">{t}</span></div>)}
          </div>
          {stats && stats.providers >= 50 && <p className="mt-6 text-sm muted">{stats.providers} professionals · {stats.reviews} reviews</p>}
        </div>
      </section>

      {featured.length > 0 && (
        <section className="bg-[#FAF9F6]">
          <div className="container-x py-16 lg:py-24">
            <span className="eyebrow">Our Top Service Providers</span>
            <h2 className="h2 mt-2 mb-8">Professionals near Marlow</h2>
            <div className="grid md:grid-cols-3 gap-5">{featured.map((p) => <ProviderCard key={p.id} p={p} />)}</div>
          </div>
        </section>
      )}

      <section className="container-x py-16 lg:py-24">
        <div className="rounded-3xl bg-[#FF8166] text-white p-8 sm:p-14 grid lg:grid-cols-[1fr_auto] gap-8 items-center relative overflow-hidden">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Are you a home-service professional?</h2>
            <p className="mt-3 text-white/90 max-w-xl">A powerful source of genuine local work. Build your profile, receive matching jobs and win more work.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/register?role=provider" className="btn bg-white text-[#D9583D] hover:bg-slate-50" data-testid="cta-join-pro-btn">Join as a Professional</Link>
            <Link to="/pricing" className="btn bg-white/15 hover:bg-white/25 text-white">See pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
